/**
 * Tour redirect utilities
 *
 * Shared functions for post-ritual-creation tour navigation.
 * Replaces 3x duplicated logic in createRitual.tsx.
 */

import type { useTourQueue } from "../hooks/useTourQueue"
import { getDemoRitualId } from "../services/tour.service"

type TourQueue = ReturnType<typeof useTourQueue>

/**
 * After creating a ritual, decide whether to start the ritual tour and redirect.
 *
 * Both paths redirect to a DEMO ritual so the user learns on a pre-built ritual.
 * The only difference is the presence of createdRitualId:
 * - **Queued (onboarding):** tour was queued during onboarding AI chat.
 *   ?tour=ritual&createdRitualId=X → after completing the demo tour, redirects
 *   to the user's created ritual.
 * - **Ensured (no prior tour record):** user has never completed/skipped a ritual
 *   tour. ?tour=ritual only → after completing the demo tour, shows profile modal.
 * - **Neither:** user already completed/skipped the tour → no tour, go to created
 *   ritual directly.
 */
export async function redirectAfterRitualCreation(
  ritualId: string,
  tourQueue: TourQueue,
): Promise<void> {
  // Check if a queued tour exists (onboarding flow)
  const triggered = await tourQueue.checkAndTriggerQueuedTour("ritual_tour", { ritualId })
  if (triggered) {
    const demoId = await getDemoRitualId()
    window.location.href = `/rituals/${demoId ?? ritualId}?tour=ritual&createdRitualId=${ritualId}`
    return
  }

  // No queued tour — user hasn't done the tour yet, start fresh on a demo
  const ensured = await tourQueue.ensureRitualTour({ ritualId })
  if (ensured) {
    const demoId = await getDemoRitualId()
    window.location.href = `/rituals/${demoId ?? ritualId}?tour=ritual&createdRitualId=${ritualId}`
    return
  }

  // User already completed/skipped — no tour
  window.location.href = `/rituals/${ritualId}`
}
